import { measuredResultCard } from "./measured-result-card.js";
import type { RoundFeedCard } from "../../shared/contracts/round-feed.js";
import type { RoundSnapshot } from "../../domain/round/round-reducer.js";
import { longDate } from "../lib/format.js";
import { roundDate } from "../../domain/round/round-date.js";
import { playersOf, scoreToPar, type PlayerView } from "../lib/round-view.js";
import { isRoundV3 } from "../../domain/round/round-types.js";
import { roundResultCardV3 } from "./round-result-card-v3.js";

/**
 * One shareable card (drawn at 1080x1920 by result-card-canvas.ts). The
 * same content feeds the PNG and the share-sheet text, so what people
 * read matches what they see.
 */
export interface ResultCard {
  poster?: {
    round: RoundFeedCard;
    avatarUrls: Record<string, string | null>;
    url: string;
  };
  memory?: { text: string; roundVersion: number };
  /** Optional existing app artwork; other card types retain their original background. */
  artwork?: string;
  /** Small spaced label above the headline, e.g. "Round result". */
  eyebrow: string;
  /** Serif headline; wrapped to the card width when long. */
  headline: string;
  /** The big line: "82 – 88", "Tied at 82" or the champion's name. */
  scoreline: string;
  /** Short note under the big line, e.g. "+10 to par". */
  scoreNote: string | null;
  /** Course and date, or "Earned 2 Sep 2026". */
  detail: string;
  /** Accent for the eyebrow and the glow; gold unless a trophy tier. */
  accent: string;
  /** Text passed to the share sheet alongside the image. */
  text: string;
  fileName: string;
}

const gold = "#c9a961";
export const cardTagline = "Every rivalry starts with one round.";
export const cardSite = "fairwayrivalry.com";

/** Card for a finished round: 1v1 winner and scores, or a solo score. */
export function roundResultCard(
  snapshot: RoundSnapshot,
  selfName: string,
): ResultCard {
  if (snapshot.rules.measuredChallenge) return measuredResultCard(snapshot);
  if (isRoundV3(snapshot)) return roundResultCardV3(snapshot);
  const players = playersOf(snapshot, selfName);
  const course = snapshot.course.name;
  const base = {
    eyebrow: "Round result",
    detail: `${course} · ${longDate(roundDate(snapshot).value) || "Date unknown"}`,
    accent: gold,
    fileName: "fairway-rivalry-result.png",
  };
  const first = players[0];
  if (!first) throw new Error("The round has no players to share.");
  if (players.length === 1) return { ...base, ...soloLines(snapshot, first) };
  const tied = snapshot.result?.tied ?? false;
  const winner = players.find((player) => player.winner) ?? first;
  const loser = players.find((player) => player !== winner) ?? first;
  const self = players.find((player) => player.isSelf);
  const rival = self ? (self === winner ? loser : winner) : null;
  const verb = tied ? "tied" : self?.winner ? "won" : "lost";
  const sentence =
    self && rival
      ? `${self.name} ${verb} vs ${rival.name}, ${gross(self)}–${gross(rival)} at ${course}.`
      : `${winner.name} ${tied ? "tied with" : "beat"} ${loser.name}, ${gross(winner)}–${gross(loser)} at ${course}.`;
  return {
    ...base,
    headline: tied
      ? `${winner.name} vs ${loser.name}`
      : `${winner.name} beat ${loser.name}`,
    scoreline: tied
      ? `Tied at ${gross(winner)}`
      : `${gross(winner)} – ${gross(loser)}`,
    scoreNote: players.length > 2 ? `${players.length} players` : null,
    text: `${sentence} ${cardTagline} ${cardSite}`,
  };
}

function soloLines(
  snapshot: RoundSnapshot,
  player: PlayerView,
): Pick<ResultCard, "headline" | "scoreline" | "scoreNote" | "text"> {
  const toPar = scoreToPar(snapshot, player.id);
  const parText =
    toPar === null ? null : toPar > 0 ? `+${toPar}` : String(toPar);
  const shot = `shot ${gross(player)}${parText ? ` (${parText})` : ""}`;
  return {
    headline: player.name,
    scoreline: gross(player),
    scoreNote: parText ? `${parText} to par` : null,
    text: `${player.name} ${shot} at ${snapshot.course.name}. ${cardTagline} ${cardSite}`,
  };
}

function gross(player: PlayerView): string {
  return player.gross === null ? "–" : String(player.gross);
}

/** Card for an earned trophy, tinted with its rarity tier. */
export function trophyCard(
  trophy: { title: string; desc: string },
  tier: { label: string; color: string },
  earnedAt: string | null,
): ResultCard {
  return {
    eyebrow: `${tier.label} trophy`,
    headline: trophy.title,
    scoreline: tier.label,
    scoreNote: trophy.desc,
    detail: earnedAt ? `Earned ${longDate(earnedAt)}` : "Earned",
    accent: tier.color,
    text: `${trophy.title}: ${trophy.desc} ${cardTagline} ${cardSite}`,
    fileName: "fairway-rivalry-trophy.png",
  };
}

/** Card for winning a Fairway Golf League season. */
export function seasonTitleCard(input: {
  year: number;
  name: string;
  points: number;
}): ResultCard {
  return {
    eyebrow: "Fairway Golf League",
    headline: `FGL ${input.year} champion`,
    scoreline: input.name,
    scoreNote: `${input.points} points`,
    detail: `Season ${input.year}`,
    accent: gold,
    text: `${input.name} won the FGL ${input.year} title with ${input.points} points. ${cardTagline} ${cardSite}`,
    fileName: "fairway-rivalry-season.png",
  };
}
